import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import '../styles/BattleshipSpectator.css';

/* ────────────────────────────────────────────
   CSS keyframes injected once into <head>
   ──────────────────────────────────────────── */
const STYLE_ID = 'ceremony-spectator-keyframes';
const KEYFRAMES = `
@keyframes ceremony-slideUp {
  0%   { opacity: 0; transform: translateY(120px) scale(0.8); }
  60%  { opacity: 1; transform: translateY(-15px) scale(1.03); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes ceremony-slideUpDramatic {
  0%   { opacity: 0; transform: translateY(200px) scale(0.6) rotate(-2deg); }
  40%  { opacity: 1; transform: translateY(-30px) scale(1.08) rotate(1deg); }
  70%  { transform: translateY(8px) scale(0.98) rotate(0deg); }
  100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
}
@keyframes ceremony-goldPulse {
  0%, 100% { text-shadow: 0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,215,0,0.2); }
  50%      { text-shadow: 0 0 40px rgba(255,215,0,0.8), 0 0 80px rgba(255,215,0,0.4), 0 0 120px rgba(255,215,0,0.2); }
}
@keyframes ceremony-megaPulse {
  0%, 100% { text-shadow: 0 0 30px rgba(255,215,0,0.6), 0 0 60px rgba(255,215,0,0.3), 0 0 100px rgba(255,215,0,0.1); transform: scale(1); }
  50%      { text-shadow: 0 0 60px rgba(255,215,0,1), 0 0 120px rgba(255,215,0,0.6), 0 0 200px rgba(255,215,0,0.3); transform: scale(1.03); }
}
@keyframes ceremony-shimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes ceremony-waitPulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50%      { opacity: 1; transform: scale(1.02); }
}
@keyframes ceremony-trophySpin {
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
}
@keyframes ceremony-trophyBounce {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.15); }
}
@keyframes ceremony-particle {
  0%   { opacity: 1; transform: translateY(0) translateX(0) scale(1); }
  100% { opacity: 0; transform: translateY(-400px) translateX(var(--px, 0px)) scale(0); }
}
@keyframes ceremony-confettiDrop {
  0%   { opacity: 1; transform: translateY(-100vh) rotate(0deg); }
  100% { opacity: 0.3; transform: translateY(100vh) rotate(720deg); }
}
@keyframes ceremony-borderGlow {
  0%, 100% { box-shadow: 0 0 15px rgba(255,215,0,0.3), inset 0 0 15px rgba(255,215,0,0.05); }
  50%      { box-shadow: 0 0 40px rgba(255,215,0,0.6), inset 0 0 30px rgba(255,215,0,0.1); }
}
@keyframes ceremony-lastPlaceWobble {
  0%, 100% { transform: rotate(0deg); }
  25%  { transform: rotate(-3deg); }
  75%  { transform: rotate(3deg); }
}
@keyframes ceremony-sadTrombone {
  0%   { transform: translateY(0); }
  30%  { transform: translateY(-10px); }
  60%  { transform: translateY(5px); }
  100% { transform: translateY(0); }
}
@keyframes ceremony-ringExpand {
  0%   { transform: scale(0.3); opacity: 0.8; border-width: 6px; }
  100% { transform: scale(3); opacity: 0; border-width: 1px; }
}
`;

function injectKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}

/* ────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────── */

function formatDollars(value) {
  if (value == null) return '$0';
  const abs = Math.abs(value);
  const formatted = abs >= 1000
    ? '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '$' + abs.toFixed(2);
  return value < 0 ? '-' + formatted : formatted;
}

function placeLabel(rank) {
  if (rank === 1) return '1ST PLACE';
  if (rank === 2) return '2ND PLACE';
  if (rank === 3) return '3RD PLACE';
  return `${rank}TH PLACE`;
}

/** Return a drama level 0-10 based on the reveal. 0 = last place, 10 = 1st place. */
function dramaLevel(person, revealIndex, totalReveals) {
  if (person.rank === 'last' || revealIndex === 0) return 0;
  // revealIndex 1..totalReveals-1 maps to increasingly dramatic
  // higher revealIndex = more dramatic
  return Math.min(10, Math.round((revealIndex / (totalReveals - 1)) * 10));
}

/* ────────────────────────────────────────────
   Particle / Confetti components (CSS only)
   ──────────────────────────────────────────── */

function GoldParticles({ count }) {
  const particles = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 1.5 + Math.random() * 2,
      px: (Math.random() - 0.5) * 300,
      size: 4 + Math.random() * 8,
    }))
  );
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {particles.current.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          bottom: '20%',
          left: `${p.left}%`,
          width: p.size,
          height: p.size,
          borderRadius: '50%',
          background: `hsl(${40 + Math.random() * 20}, 100%, ${50 + Math.random() * 20}%)`,
          animation: `ceremony-particle ${p.duration}s ease-out ${p.delay}s infinite`,
          '--px': `${p.px}px`,
        }} />
      ))}
    </div>
  );
}

function Confetti({ count }) {
  const pieces = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 4,
      color: ['#ffd700', '#ff4444', '#44ff44', '#4488ff', '#ff44ff', '#ffaa00', '#ffffff'][i % 7],
      width: 6 + Math.random() * 10,
      height: 4 + Math.random() * 6,
    }))
  );
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      {pieces.current.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          top: 0,
          left: `${p.left}%`,
          width: p.width,
          height: p.height,
          background: p.color,
          opacity: 0.8,
          animation: `ceremony-confettiDrop ${p.duration}s linear ${p.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

function ExpandingRings() {
  return (
    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0 }}>
      {[0, 0.6, 1.2, 1.8].map((delay, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 200,
          height: 200,
          borderRadius: '50%',
          border: '4px solid rgba(255, 215, 0, 0.4)',
          animation: `ceremony-ringExpand 2.4s ease-out ${delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────
   Main Component
   ──────────────────────────────────────────── */

const EXAMPLE_PEOPLE = [
  { rank: 'last', username: 'PaperHands420', portfolioValue: 2150, profit: -7850, totalTrades: 47, topStock: 'MEME', totalGains: 1200, totalLosses: 9050 },
  { rank: 10, username: 'BagHolder99', portfolioValue: 11200, profit: 1200, totalTrades: 12, topStock: 'DOGE', totalGains: 3400, totalLosses: 2200 },
  { rank: 9, username: 'StonkLord', portfolioValue: 12800, profit: 2800, totalTrades: 23, topStock: 'YOLO', totalGains: 5100, totalLosses: 2300 },
  { rank: 8, username: 'CryptoKaren', portfolioValue: 14500, profit: 4500, totalTrades: 31, topStock: 'KOOK', totalGains: 7200, totalLosses: 2700 },
  { rank: 7, username: 'YOLO_Queen', portfolioValue: 16300, profit: 6300, totalTrades: 18, topStock: 'Y2K', totalGains: 8100, totalLosses: 1800 },
  { rank: 6, username: 'TendiesChef', portfolioValue: 18900, profit: 8900, totalTrades: 35, topStock: 'CHAD', totalGains: 11400, totalLosses: 2500 },
  { rank: 5, username: 'WallStWolf', portfolioValue: 21400, profit: 11400, totalTrades: 28, topStock: 'FOMO', totalGains: 14200, totalLosses: 2800 },
  { rank: 4, username: 'DiamondDave', portfolioValue: 25700, profit: 15700, totalTrades: 42, topStock: 'HODL', totalGains: 19500, totalLosses: 3800 },
  { rank: 3, username: 'MoonMaster', portfolioValue: 31200, profit: 21200, totalTrades: 55, topStock: 'MOON', totalGains: 26000, totalLosses: 4800 },
  { rank: 2, username: 'BullRunBenny', portfolioValue: 42500, profit: 32500, totalTrades: 38, topStock: 'BULL', totalGains: 37200, totalLosses: 4700 },
  { rank: 1, username: 'RiskTaker99', portfolioValue: 58300, profit: 48300, totalTrades: 61, topStock: 'YOLO', totalGains: 52100, totalLosses: 3800 },
];

const CeremonySpectatorPage = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const isExample = searchParams.has('example');
  const [connected, setConnected] = useState(false);
  const [currentReveal, setCurrentReveal] = useState(null); // { person, revealIndex, totalReveals }
  const [ceremonyStarted, setCeremonyStarted] = useState(false);
  const [animKey, setAnimKey] = useState(0); // bump to re-trigger animations
  const socketRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(-1);

  useEffect(() => {
    injectKeyframes();
  }, []);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    const socket = io(API_BASE_URL, {
      auth: { public: true },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('ceremony-start', () => {
      setCeremonyStarted(true);
      setCurrentReveal(null);
      setAnimKey(k => k + 1);
    });

    socket.on('ceremony-reveal', (data) => {
      setCeremonyStarted(true);
      setCurrentReveal(data);
      setAnimKey(k => k + 1);
    });

    socket.on('ceremony-reset', () => {
      setCeremonyStarted(false);
      setCurrentReveal(null);
      setAnimKey(k => k + 1);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Example mode: advance through fake data
  const advanceExample = () => {
    const nextIdx = exampleIndex + 1;
    if (nextIdx >= EXAMPLE_PEOPLE.length) {
      setExampleIndex(-1);
      setCeremonyStarted(false);
      setCurrentReveal(null);
      setAnimKey(k => k + 1);
      return;
    }
    setExampleIndex(nextIdx);
    setCeremonyStarted(true);
    setCurrentReveal({
      person: EXAMPLE_PEOPLE[nextIdx],
      revealIndex: nextIdx,
      totalReveals: EXAMPLE_PEOPLE.length,
    });
    setAnimKey(k => k + 1);
  };

  /* ── Shared UI pieces ── */

  const renderConnectionStatus = () => (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      <span className="connection-dot"></span>
      <span className="connection-text">{connected ? 'LIVE' : 'RECONNECTING...'}</span>
    </div>
  );

  const fullscreenBtn = !isFs && (
    <button onClick={() => document.documentElement.requestFullscreen()} style={{
      position: 'fixed', top: 8, left: 8, zIndex: 99999,
      background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
      color: '#00ff41', padding: '4px 10px', fontSize: '11px',
      fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
    }}>FULLSCREEN</button>
  );

  const backNav = !isEmbed && (
    <div className="spectator-back-nav">
      <Link to="/spectate" className="back-link">&larr; Back to Spectate</Link>
    </div>
  );

  /* ── Waiting state ── */
  if (!ceremonyStarted || !currentReveal) {
    return (
      <div className="spectator-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        {fullscreenBtn}
        {backNav}
        {renderConnectionStatus()}

        <div style={{ textAlign: 'center', position: 'relative' }}>
          <GoldParticles count={20} />

          {/* Trophy icon */}
          <div style={{
            fontSize: '100px',
            marginBottom: '30px',
            animation: 'ceremony-trophyBounce 2s ease-in-out infinite',
          }}>
            &#127942;
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '72px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '12px',
            margin: '0 0 20px 0',
            background: 'linear-gradient(90deg, #ffd700, #fff8dc, #ffd700, #daa520, #ffd700)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'ceremony-shimmer 3s linear infinite, ceremony-goldPulse 2s ease-in-out infinite',
            fontFamily: 'Courier New, monospace',
          }}>
            AWARD CEREMONY
          </h1>

          {/* Subtitle */}
          <div style={{
            fontSize: '24px',
            color: '#888',
            letterSpacing: '6px',
            textTransform: 'uppercase',
            fontFamily: 'Courier New, monospace',
            marginBottom: '40px',
          }}>
            WALL STREET PARTY
          </div>

          {/* Decorative divider */}
          <div style={{
            width: '200px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #ffd700, transparent)',
            margin: '0 auto 30px auto',
          }} />

          {/* Tagline */}
          <div style={{
            fontSize: '18px',
            color: '#aaa',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            fontFamily: 'Courier New, monospace',
            marginBottom: '40px',
            lineHeight: '1.8',
          }}>
            Who made the most? Who lost it all?
          </div>

          {/* Waiting text */}
          <div style={{
            fontSize: '20px',
            color: '#666',
            letterSpacing: '4px',
            textTransform: 'uppercase',
            fontFamily: 'Courier New, monospace',
            animation: 'ceremony-waitPulse 2s ease-in-out infinite',
          }}>
            {ceremonyStarted ? 'Get ready...' : 'Waiting to begin...'}
          </div>

          <div className="waiting-animation" style={{ justifyContent: 'center', marginTop: '30px' }}>
            <span className="waiting-dot"></span>
            <span className="waiting-dot"></span>
            <span className="waiting-dot"></span>
          </div>

          {isExample && (
            <button onClick={advanceExample} style={{
              marginTop: '40px',
              padding: '16px 40px',
              fontSize: '20px',
              fontWeight: 'bold',
              fontFamily: 'Courier New, monospace',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}>
              NEXT PERSON &rarr;
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ── Reveal state ── */
  const { person, revealIndex, totalReveals } = currentReveal;
  const drama = dramaLevel(person, revealIndex, totalReveals);
  const isLastPlace = person.rank === 'last' || revealIndex === 0;
  const isFirst = person.rank === 1;
  const isTopThree = !isLastPlace && person.rank <= 3;
  const profit = person.profit != null ? person.profit : 0;
  const isPositiveProfit = profit >= 0;

  // Scale effects by drama
  const glowIntensity = Math.max(0.2, drama / 10);
  const particleCount = isFirst ? 60 : isTopThree ? 35 : Math.max(0, drama * 3);
  const trophySize = isLastPlace ? 60 : isFirst ? 160 : 60 + (drama * 10);
  const nameSize = isLastPlace ? 48 : isFirst ? 80 : 40 + (drama * 4);
  const labelSize = isLastPlace ? 36 : isFirst ? 56 : 28 + (drama * 3);

  // Colors per tier
  const labelColor = isLastPlace ? '#888' : isFirst ? '#ffd700' : isTopThree ? '#ffd700' : '#aaa';
  const nameColor = isLastPlace ? '#999' : '#fff';
  const borderColor = isLastPlace
    ? 'rgba(150, 150, 150, 0.3)'
    : isFirst
      ? 'rgba(255, 215, 0, 0.6)'
      : isTopThree
        ? 'rgba(255, 215, 0, 0.4)'
        : `rgba(255, 215, 0, ${0.1 + glowIntensity * 0.3})`;

  const cardBg = isLastPlace
    ? 'rgba(40, 40, 50, 0.9)'
    : isFirst
      ? 'rgba(50, 45, 20, 0.9)'
      : 'rgba(30, 30, 40, 0.9)';

  const trophyIcon = isLastPlace ? '\u{1F4A9}' : isFirst ? '\u{1F3C6}' : isTopThree ? '\u{1F3C6}' : '\u{2B50}';

  const slideAnim = drama >= 7
    ? `ceremony-slideUpDramatic 1s ease-out`
    : `ceremony-slideUp 0.7s ease-out`;

  const glowAnim = isFirst
    ? 'ceremony-megaPulse 1.5s ease-in-out infinite'
    : drama >= 4
      ? `ceremony-goldPulse ${3 - drama * 0.15}s ease-in-out infinite`
      : 'none';

  const borderAnim = isFirst
    ? 'ceremony-borderGlow 1.5s ease-in-out infinite'
    : isTopThree
      ? 'ceremony-borderGlow 2s ease-in-out infinite'
      : 'none';

  const placeText = isLastPlace ? 'LAST PLACE' : placeLabel(person.rank);

  return (
    <div className="spectator-page" style={{ justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
      {fullscreenBtn}
      {backNav}
      {renderConnectionStatus()}

      {/* Background particles */}
      {particleCount > 0 && <GoldParticles count={particleCount} />}
      {isFirst && <Confetti count={50} />}
      {isFirst && <ExpandingRings />}

      {/* Reveal card */}
      <div
        key={animKey}
        style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          padding: isFirst ? '60px 80px' : '50px 70px',
          background: cardBg,
          border: `3px solid ${borderColor}`,
          borderRadius: '20px',
          maxWidth: isFirst ? '900px' : '750px',
          width: '90%',
          animation: `${slideAnim}, ${borderAnim}`,
        }}
      >
        {/* Place label */}
        <div style={{
          fontSize: labelSize,
          fontWeight: 'bold',
          color: labelColor,
          letterSpacing: isFirst ? '16px' : '8px',
          textTransform: 'uppercase',
          fontFamily: 'Courier New, monospace',
          marginBottom: '20px',
          animation: isLastPlace ? 'ceremony-lastPlaceWobble 0.6s ease-in-out 3' : glowAnim,
        }}>
          {placeText}
        </div>

        {/* Consolation subtitle for last place */}
        {isLastPlace && (
          <div style={{
            fontSize: '18px',
            color: '#666',
            letterSpacing: '4px',
            fontFamily: 'Courier New, monospace',
            marginBottom: '20px',
            textTransform: 'uppercase',
          }}>
            &ldquo;Thanks for participating&rdquo;
          </div>
        )}

        {/* Profile Picture / Trophy */}
        {person.profilePicture ? (
          <div style={{ marginBottom: '25px', position: 'relative', display: 'inline-block' }}>
            <img
              src={person.profilePicture}
              alt={person.username}
              style={{
                width: isFirst ? 160 : isTopThree ? 130 : isLastPlace ? 100 : 110,
                height: isFirst ? 160 : isTopThree ? 130 : isLastPlace ? 100 : 110,
                borderRadius: '50%',
                objectFit: 'cover',
                border: `4px solid ${isLastPlace ? '#555' : isFirst ? '#ffd700' : isTopThree ? '#ffd700' : '#888'}`,
                boxShadow: isFirst
                  ? '0 0 40px rgba(255,215,0,0.6), 0 0 80px rgba(255,215,0,0.3)'
                  : isTopThree
                    ? '0 0 25px rgba(255,215,0,0.4)'
                    : '0 0 15px rgba(255,255,255,0.1)',
                animation: isFirst
                  ? 'ceremony-megaPulse 2s ease-in-out infinite'
                  : isLastPlace
                    ? 'ceremony-sadTrombone 1s ease-in-out 2'
                    : 'ceremony-trophyBounce 2s ease-in-out infinite',
              }}
            />
            {/* Small trophy badge for top 3 */}
            {isTopThree && (
              <div style={{
                position: 'absolute', bottom: -5, right: -5,
                fontSize: isFirst ? 48 : 36,
                filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.8))',
              }}>
                {trophyIcon}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            fontSize: trophySize,
            marginBottom: '25px',
            animation: isLastPlace
              ? 'ceremony-sadTrombone 1s ease-in-out 2'
              : isFirst
                ? 'ceremony-trophySpin 3s ease-in-out infinite'
                : 'ceremony-trophyBounce 2s ease-in-out infinite',
          }}>
            {trophyIcon}
          </div>
        )}

        {/* Username */}
        <div style={{
          fontSize: nameSize,
          fontWeight: 'bold',
          color: nameColor,
          letterSpacing: isFirst ? '10px' : '6px',
          textTransform: 'uppercase',
          fontFamily: 'Courier New, monospace',
          marginBottom: '25px',
          ...(isFirst ? {
            background: 'linear-gradient(90deg, #ffd700, #fff8dc, #ffd700, #daa520, #ffd700)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'ceremony-shimmer 2s linear infinite',
          } : isTopThree ? {
            background: 'linear-gradient(90deg, #ffd700, #fff, #ffd700)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'ceremony-shimmer 3s linear infinite',
          } : {}),
          wordBreak: 'break-word',
        }}>
          {person.username}
        </div>

        {/* Divider */}
        <div style={{
          width: '60%',
          height: '2px',
          background: isLastPlace
            ? 'rgba(150,150,150,0.2)'
            : `linear-gradient(90deg, transparent, ${labelColor}, transparent)`,
          margin: '0 auto 25px auto',
        }} />

        {/* Portfolio Value */}
        <div style={{
          fontSize: isFirst ? 52 : isTopThree ? 44 : 36,
          fontWeight: 'bold',
          color: isLastPlace ? '#888' : isFirst ? '#ffd700' : '#fff',
          fontFamily: 'Courier New, monospace',
          letterSpacing: '4px',
          marginBottom: '12px',
          animation: isFirst ? 'ceremony-goldPulse 2s ease-in-out infinite' : 'none',
        }}>
          {formatDollars(person.portfolioValue)}
        </div>

        {/* Profit */}
        <div style={{
          fontSize: isFirst ? 28 : 22,
          fontWeight: 'bold',
          color: isPositiveProfit ? '#00ff88' : '#ff4444',
          fontFamily: 'Courier New, monospace',
          letterSpacing: '2px',
        }}>
          {isPositiveProfit ? '+' : ''}{formatDollars(profit)}
          <span style={{ color: '#666', fontSize: '16px', marginLeft: '10px' }}>
            PROFIT
          </span>
        </div>

        {/* Trading Stats */}
        {(person.totalTrades > 0 || person.topStock) && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: isFirst ? '40px' : '30px',
            marginTop: '20px',
            flexWrap: 'wrap',
          }}>
            {person.totalTrades > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: isFirst ? 28 : 22,
                  fontWeight: 'bold',
                  color: isLastPlace ? '#777' : '#fff',
                  fontFamily: 'Courier New, monospace',
                }}>
                  {person.totalTrades}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontFamily: 'Courier New, monospace',
                }}>TRADES</div>
              </div>
            )}
            {person.topStock && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: isFirst ? 28 : 22,
                  fontWeight: 'bold',
                  color: isLastPlace ? '#777' : '#00bfff',
                  fontFamily: 'Courier New, monospace',
                }}>
                  ${person.topStock}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontFamily: 'Courier New, monospace',
                }}>TOP STOCK</div>
              </div>
            )}
            {person.totalGains > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: isFirst ? 28 : 22,
                  fontWeight: 'bold',
                  color: '#00ff88',
                  fontFamily: 'Courier New, monospace',
                }}>
                  {formatDollars(person.totalGains)}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontFamily: 'Courier New, monospace',
                }}>WON</div>
              </div>
            )}
            {person.totalLosses > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: isFirst ? 28 : 22,
                  fontWeight: 'bold',
                  color: '#ff4444',
                  fontFamily: 'Courier New, monospace',
                }}>
                  {formatDollars(person.totalLosses)}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontFamily: 'Courier New, monospace',
                }}>LOST</div>
              </div>
            )}
          </div>
        )}

        {/* Fun text for last place */}
        {isLastPlace && (
          <div style={{
            marginTop: '30px',
            fontSize: '16px',
            color: '#555',
            fontFamily: 'Courier New, monospace',
            letterSpacing: '3px',
            textTransform: 'uppercase',
          }}>
            Better luck next time on Wall Street
          </div>
        )}

        {/* Champion text for 1st place */}
        {isFirst && (
          <div style={{
            marginTop: '30px',
            fontSize: '22px',
            fontWeight: 'bold',
            letterSpacing: '8px',
            textTransform: 'uppercase',
            fontFamily: 'Courier New, monospace',
            background: 'linear-gradient(90deg, #ffd700, #fff, #ffd700)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'ceremony-shimmer 2s linear infinite',
          }}>
            &#9733; WOLF OF WALL STREET &#9733;
          </div>
        )}
      </div>

      {isExample && (
        <button onClick={advanceExample} style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          zIndex: 9999,
          padding: '16px 40px',
          fontSize: '20px',
          fontWeight: 'bold',
          fontFamily: 'Courier New, monospace',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
          color: '#000',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)',
        }}>
          {exampleIndex >= EXAMPLE_PEOPLE.length - 1 ? 'RESET' : 'NEXT PERSON'} &rarr;
        </button>
      )}
    </div>
  );
};

export default CeremonySpectatorPage;
